import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from '../components/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover';
import { Searcher } from '@worldcoo/shared/searcher';

export function Combobox(props: {
	ref?: React.Ref<HTMLButtonElement>;
	value: string;
	onChange: (value: string, selectedValue: string | undefined) => void;
	options: { value: string; label: string }[];
	placeholder: string;
	searchPlaceholder: string;
	commandEmpty: string;
	creatable?: boolean;
	children?: React.ReactNode;
	isError?: boolean;
}) {
	const [open, setOpen] = useState(false);

	const optionsIds = props.options.map(({ value }) => value);

	const options = props.creatable
		? [...props.options, ...(!optionsIds.includes(props.value) ? [{ value: props.value, label: props.value }] : [])]
		: props.options;

	const labelByValue = new Map(options.map(({ value, label }) => [value, label]));

	const [searchValue, setSearchValue] = useState('');

	const createTag = () => {
		if (props.creatable) {
			if (props.value === searchValue) return;
			props.onChange(searchValue, searchValue);
			setSearchValue('');
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				{props.children ?? (
					<Button
						ref={props.ref}
						variant="outline"
						role="combobox"
						aria-expanded={open}
						aria-invalid={props.isError || undefined}
						className="h-auto min-h-12 w-full justify-start truncate p-0 text-wrap"
					>
						<span
							className={cn(
								'text-muted-foreground absolute top-0 origin-top-left py-[14px] font-medium transition-transform duration-300',
								props.value.length > 0 && '-translate-y-1.5 scale-[85%]',
							)}
						>
							{props.placeholder}
						</span>

						<span className="text-foreground w-full truncate pt-5.5 pr-4 pb-1 text-start font-normal text-wrap">
							{labelByValue.get(props.value)}
						</span>

						<ChevronDownIcon className="absolute top-4 right-3" />
					</Button>
				)}
			</PopoverTrigger>
			<PopoverContent className="p-0">
				<Command
					filter={(value, search, keywords) => {
						return [value, ...(keywords ?? [])]
							.map((value) => Searcher.normalizeString(value))
							.some((value) => value.includes(Searcher.normalizeString(search)))
							? 1
							: 0;
					}}
				>
					<CommandInput
						placeholder={props.searchPlaceholder}
						value={searchValue}
						onValueChange={setSearchValue}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								event.preventDefault();
								createTag();
							}
						}}
					/>
					<CommandList>
						<CommandEmpty>
							{!props.creatable ? (
								props.commandEmpty
							) : (
								<Button
									variant={null}
									className="inline text-wrap hover:underline"
									onClick={() => {
										if (props.value === searchValue) return;
										props.onChange(searchValue, searchValue);
									}}
								>
									{props.commandEmpty}
								</Button>
							)}
						</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									keywords={[option.label, option.value]}
									onSelect={(currentValue) => {
										props.onChange(option.value, props.value === currentValue ? undefined : currentValue);
									}}
								>
									{option.label}
									<div className={cn('ml-auto rounded-sm border', props.value === option.value && 'border-primary')}>
										<CheckIcon
											className={cn(
												'bg-primary text-background size-4 shrink-0',
												props.value === option.value ? 'opacity-100' : 'opacity-0',
											)}
										/>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
